import Vue from 'vue'
import Vuex from 'vuex'
import router from './routes/index'
import { logUserAction } from './utils/logging'
import screens from './utils/pageSchema'
Vue.use(Vuex)

const store = new Vuex.Store({
  state: {
    originalData: {},
    baseData: {},
    postLogin: null,
    wftkn: '',
    api: '',
    logins: [],
    title1: '',
    fields: '',
    textColor: '#000',
    preError: false,
    preErrorPayload: {},
    isRejeted: false,
    welcomeBack: false,
    welcomeBackPayload: {},
    connecting: false,
    freeOver: false,
    plans: [],
    lastPostMessage: '',
    skipToFreeWifi: false,
    plumeLoginErrorMessage: '',
    isPlume: false,
    contractVersions: ['xxxxxxxx', 'xxxxxxx', '20240307'], // add new version at the end and remove 0th index version, max 3 versions allowed
    consentAgreed: undefined,
  },
  mutations: {
    skipToFreeWifi(state) {
      state.skipToFreeWifi = true
    },
    setOriginalData(state, data) {
      state.originalData = data
    },
    setBaseData(state, data) {
      // Use Vue.set to ensure deep reactivity for nested properties
      Vue.set(state, 'baseData', data)
      sessionStorage.setItem('baseData', JSON.stringify(data))
    },
    clearBaseData(state) {
      state.baseData = {}
    },
    setPostLogin(state, data) {
      state.postLogin = data
    },
    setPlumeLoginError(state, data) {
      state.plumeLoginErrorMessage = data
    },
    setWftkn(state, data) {
      state.wftkn = data
    },
    setApi(state, data) {
      state.api = data
    },
    setLogins(state, data) {
      state.logins = data
    },
    setTitle1(state, data) {
      state.title1 = data
    },
    setFields(state, data) {
      state.fields = data
    },
    setTextColor(state, data) {
      state.textColor = data
    },
    setPreError(state, data) {
      state.preError = data
    },
    setPreErrorPayload(state, data) {
      state.preErrorPayload = data
    },
    setIsRejeted(state, data) {
      state.isRejeted = data
    },
    setWelcomeBackPayload(state, data) {
      state.welcomeBackPayload = data
    },
    setWelcomeBack(state, data) {
      state.welcomeBack = data
    },
    setConnecting(state, data) {
      state.connecting = data
    },
    setFreeOver(state, data) {
      state.freeOver = data
    },
    setPaymentPlans(state, data) {
      state.plans = data
    },
    setLastPostMessage(state, data) {
      state.lastPostMessage = data
    },
    setIsPlume(state, data) {
      state.isPlume = data
    },
    setAttemptCount(state, data) {
      state.attemptCount = data
    },
  },
  getters: {
    postLogin: (state) => {
      return state.postLogin
    },
    getContractVersions: (state) => {
      return state.contractVersions
    },
    isPlume: (state) => {
      return state.isPlume
    },
    isConsentAgreed: (state) => {
      return state.consentAgreed
    },
  },
  actions: {
    fetchPostLogin(context, wftkn) {
      logUserAction({
        type: 'api_request',
        label: 'Post Login API Request',
        data: { wftkn },
      })

      const params = []
      const postLogin = context.state.baseData.postLogin
      const flags = context.state.baseData.flags
      let hasRedirect = false
      let hasDynamicParams = false
      let isSucid = false
      let sucidValue = ''
      if (postLogin && postLogin.redirect && postLogin.redirect.options) {
        const opts = postLogin.redirect.options
        hasRedirect = opts.action === 'URL'
        hasDynamicParams = opts.dynamicParam
      }

      if (flags && flags.isSucid) {
        isSucid = true
        sucidValue = localStorage.getItem('SUCID') || ''
      }

      if (hasRedirect) params.push('redirect_url=1')
      if (isSucid && sucidValue) params.push(`SUCID=${encodeURIComponent(sucidValue)}`)
      if (hasDynamicParams) params.push('dynamic_params=1')
      const paramString = params.length ? '&' + params.join('&') : ''

      logUserAction({
        type: 'api_request',
        label: '[STORE] fetchPostLogin - API call starting',
        data: {
          wftkn,
          url: `/index.php?controller=landingpage&action=post-login-payload&wftkn=${wftkn}${paramString}`,
          params: paramString,
        },
      })

      axios
        .get(
          `/index.php?controller=landingpage&action=post-login-payload&wftkn=${wftkn}${paramString}`,
        )
        .then(async (res) => {
          context.commit('setPostLogin', res.data)
          sessionStorage.setItem('postLogin', JSON.stringify(res.data))

          logUserAction({
            type: 'api_response',
            label: '[STORE] fetchPostLogin - Backend response received',
            data: {
              hasData: !!res.data,
              postLoginSaved: true,
            },
          })

          const currentRoute = router.currentRoute.path

          logUserAction({
            type: 'navigation',
            label: '[STORE] fetchPostLogin - Triggering navigation to thankyou',
            data: {
              currentRoute,
              nextPage: 'thankyou',
              reason:
                'Backend response received, navigating directly to thankyou (post-login flow)',
            },
          })

          await screens.navigateTo('thankyou')
        })
        .catch((error) => {
          logUserAction({
            type: 'error',
            label: '[STORE] fetchPostLogin - API Request Failed',
            data: {
              error: error.message,
              status: error.response?.status,
              url: `/index.php?controller=landingpage&action=post-login-payload&wftkn=${wftkn}${paramString}`,
            },
          })
        })
    },
    async fetchPlumeLogin(context, data) {
      const attemptCount = context.state.attemptCount || 1
      logUserAction({
        type: 'api_request',
        label: 'Plume Login API Request',
        data: { attemptCount, url: data.url },
      })

      try {
        await axios.get(
          `/?controller=proxy-request&action=fetchPlumeLogin&url=${data.url}&body=${JSON.stringify(
            data.body,
          )}`,
        )
        logUserAction({
          type: 'navigation',
          label: 'Plume Login Successful - Redirecting User',
        })
        window.location = data.redirect_url
      } catch (error) {
        logUserAction({
          type: 'error',
          label: 'Plume Login Failed',
          data: {
            error: error.message,
            status: error.response?.status,
            attemptCount,
          },
        })

        if (error.response && error.response.data && error.response.status === 400) {
          context.commit('setPlumeLoginError', 'request_failed__action_undefined')
        } else if (error.response && error.response.status === 417 && attemptCount < 2) {
          context.commit('setPlumeLoginError', error.response.data.meta)
          context.commit('setAttemptCount', attemptCount + 1)
          setTimeout(() => {
            context.dispatch('fetchPlumeLogin', data)
          }, 3000)
        } else if (error.response && error.response.status === 417 && attemptCount >= 2) {
          logUserAction({
            type: 'navigation',
            label: 'Redirecting to Retry Connection Page',
          })
          await screens.navigateTo('retry-connection')
        } else if (
          error.response &&
          error.response.data &&
          error.response.status === 417 &&
          error.response.data.request_id
        ) {
          context.commit(
            'setPlumeLoginError',
            `Request failed. Request ID: ${error.response.data.request_id}`,
          )
        } else {
          context.commit('setPlumeLoginError', `Request failed`)
        }
      }
    },
    agreeConsent(context, data) {
      logUserAction({
        type: 'user_action',
        label: 'User Agreed to Terms and Conditions',
        data: { consent: data.consent },
      })

      context.state.consentAgreed = data.consent
      return new Promise((resolve) => {
        axios
          .post(
            '/index.php?controller=landingpage&action=post-register-user-consent&UserConsent=' +
              data.consent +
              '&wftkn=' +
              data.wftkn,
          )
          .then((res) => {
            logUserAction({
              type: 'api_response',
              label: 'User Consent Successfully Saved',
            })
            resolve()
          })
      })
    },
  },
})

const savedPostLogin = JSON.parse(sessionStorage.getItem('postLogin'))
if (savedPostLogin) {
  store.commit('setPostLogin', savedPostLogin)
}

const savedBaseData = JSON.parse(sessionStorage.getItem('baseData'))
if (savedBaseData) {
  store.commit('setBaseData', savedBaseData)
}

export default store
